import {toast} from 'sonner';
import {useState} from 'react';

import {StoredFilterType} from 'modules/decisionsNext/api/types';
import {UseStoredFilters, UseStoredFiltersActions} from 'filters/features/types';

import {clearFilter} from 'filters/utils';
import {useReorderSavedFiltersMutation} from 'features/GlobalFilterProvider/api/reorderFilters.generated';
import {PageFilter} from 'filters';
import {confirmation} from 'utils/confirmation';
import {generateDecisionsShareFiltersPath} from 'core/constants/paths';
import {useSourceFilter} from 'filters/contexts/FilterStackProvider';
import {pushPolicyFilterToRecent, pushPolicyFilterToSaved} from 'features/GlobalFilterProvider/utils/cache';
import {useUpsertPolicyFilterMutation} from 'features/GlobalFilterProvider/api/policyFilterMutations.generated';
import {useDeleteFilterMutation, useUpdateFilterLastUsedMutation} from 'api/filters/filterMutations.generated';
import {PolicyFilterTag} from 'constants/policyFilterTags';
import {
  RecentPolicyFiltersDocument,
  SavedPolicyFiltersDocument,
  SavedPolicyFiltersQuery,
  SavedPolicyFiltersQueryVariables,
} from 'features/GlobalFilterProvider/api/policyFilters.generated';
import {useGlobalStoredFilters} from 'features/GlobalFilterProvider/hooks/useGlobalStoredFilters';

const generateShareFiltersURL = (id: string) => {
  return `${location.origin}${generateDecisionsShareFiltersPath({id})}`;
};

export const createUseStoredPolicyFiltersActions: (
  tag: PolicyFilterTag,
  useStoredFilters: UseStoredFilters,
  options?: {skipShareFilters?: boolean}
) => UseStoredFiltersActions = (tag, useStoredFilters, options) => () => {
  const [isSaving, setIsSaving] = useState(false);
  const {
    savedFilters: {saved},
  } = useStoredFilters();
  const filter = useSourceFilter();
  const [upsertPolicyFilter] = useUpsertPolicyFilterMutation();
  const [deleteFilter] = useDeleteFilterMutation();
  const [reorderFilters] = useReorderSavedFiltersMutation();
  const [updateLastUsed] = useUpdateFilterLastUsedMutation();

  const handleReorder = (reorderedList: string[]) => {
    void reorderFilters({
      variables: {
        reorderedList,
      },
      optimisticResponse: {
        reorderFilters: {success: true},
      },
      update: cache => {
        cache.updateQuery<SavedPolicyFiltersQuery, SavedPolicyFiltersQueryVariables>(
          {
            query: SavedPolicyFiltersDocument,
            variables: {tag},
          },
          data => {
            if (!data) {
              return null;
            }

            return {
              storedFiltersV2: {
                policyFiltersList: reorderedList
                  .map(id => data.storedFiltersV2.policyFiltersList.find(f => f.id === id))
                  .filter(Boolean),
              },
            } satisfies SavedPolicyFiltersQuery;
          }
        );
      },
    });
  };

  const handleDelete = async (id: string) => {
    const isFilterCurrentlyApplied = filter && 'storedId' in filter ? filter.storedId === id : false;

    // We add recent filter identical to the deleted saved filter so that user doesn't lose the filter settings
    if (isFilterCurrentlyApplied && filter) {
      await upsertPolicyFilter({
        variables: {
          input: {
            tag,
            type: StoredFilterType.Recent,
            filter: clearFilter(filter),
          },
        },
        optimisticResponse: {
          upsertPolicyFilter: {
            id: 'temp-id',
            name: 'Recent Filter',
            lastUsed: new Date().toISOString(),
            type: StoredFilterType.Recent,
            tag,
          },
        },
        update(cache) {
          pushPolicyFilterToRecent({
            tag,
            cache,
            filter: {
              id: 'temp-id',
              name: 'Recent Filter',
              filter,
            },
            type: StoredFilterType.Recent,
          });
        },
      });
    }

    await deleteFilter({
      variables: {
        id,
      },
      refetchQueries: [
        {
          query: SavedPolicyFiltersDocument,
          variables: {tag},
        },
        {
          query: RecentPolicyFiltersDocument,
          variables: {tag},
        },
      ],
    });
  };

  const handleRename = async (id: string, newName: string) => {
    const filter = saved?.find(filter => filter.id === id);

    if (!filter) {
      throw new Error(`Filter not found: ${id}; ${newName}`);
    }

    await upsertPolicyFilter({
      variables: {
        input: {
          id,
          tag,
          type: StoredFilterType.Saved,
          name: newName,
          filter: clearFilter(filter.filter),
        },
      },
    });
  };

  const handleSave = async (filter: PageFilter, name: string) => {
    const savedFilter = saved?.find(filter => filter.name === name);

    if (savedFilter) {
      await confirmation({
        content: 'By saving, you will update the current filter settings.',
      });

      setIsSaving(true);

      await upsertPolicyFilter({
        variables: {
          input: {
            tag,
            name,
            id: savedFilter.id,
            type: StoredFilterType.Saved,
            filter: clearFilter(filter),
          },
        },
      });

      await updateLastUsed({
        variables: {
          id: savedFilter.id,
        },
        refetchQueries: [
          {
            query: SavedPolicyFiltersDocument,
            variables: {tag},
          },
          {
            query: RecentPolicyFiltersDocument,
            variables: {tag},
          },
        ],
      });

      toast.success(
        <>
          Filter <strong>{name}</strong> successfully updated
        </>
      );

      setIsSaving(false);

      return savedFilter.id;
    }

    setIsSaving(true);

    const {data} = await upsertPolicyFilter({
      variables: {
        input: {
          tag,
          name,
          type: StoredFilterType.Saved,
          filter: clearFilter(filter),
        },
      },
      refetchQueries: [
        {
          query: SavedPolicyFiltersDocument,
          variables: {tag},
        },
        {
          query: RecentPolicyFiltersDocument,
          variables: {tag},
        },
      ],
      // Write the new filter into the saved filters cache immediately so consumers that
      // reference it by id (e.g. custom home sections) resolve its name without flashing
      // while the refetch is in flight.
      update(cache, {data}) {
        const id = data?.upsertPolicyFilter.id;

        if (!id) {
          return;
        }

        pushPolicyFilterToSaved({
          tag,
          cache,
          filter: {id, name, filter},
        });
      },
    });

    toast.success(
      <>
        Filter <strong>{name}</strong> successfully saved
      </>
    );

    setIsSaving(false);

    return data?.upsertPolicyFilter.id;
  };

  const handleShare = async (id: string) => {
    try {
      await navigator.clipboard.writeText(generateShareFiltersURL(id));

      toast.success('Active filters copied to clipboard');
    } catch {
      toast.error('Active filters copying was failed');
    }
  };

  return {
    isSaving,
    saveFilter: handleSave,
    deleteSavedFilter: handleDelete,
    renameSavedFilter: handleRename,
    reorderSavedFilters: handleReorder,
    shareFilter: tag === PolicyFilterTag.GLOBAL && !options?.skipShareFilters ? handleShare : undefined,
  };
};

export const useGlobalStoredFiltersActions = createUseStoredPolicyFiltersActions(
  PolicyFilterTag.GLOBAL,
  useGlobalStoredFilters
);
